import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { Theme, useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import { pick } from "lodash";
import React, { useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { Series, Module, ISaveModule } from "services/KnowfullyAdminClient";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
}));

const { Form } = withTypes<ISaveModule>();

type ModuleFormProps = {
  onSubmit: (values: ISaveModule) => void;
  onCancel?: () => void;
  series: Series;
  module?: Module;
  submitLabel: string;
};

const ModuleForm = ({
  onCancel,
  onSubmit,
  series,
  module,
  submitLabel,
}: ModuleFormProps & FormProps<ISaveModule>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());

  const init = useMemo(
    () => ({
      ...pick(
        module ?? {
          name: "",
          quantity: 0,
        },
        ["id", "name", "quantity"]
      ),
    }),
    [module]
  );

  return useMemo(
    () => (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError, values }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Field name="name" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "nameLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="quantity" type="number">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "moduleQuantityLabel" })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button disabled={submitting} onClick={onCancel}>
                {t({ id: "cancelButton" })}
              </Button>

              <Button type="submit" color="primary" disabled={submitting}>
                {submitLabel}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    ),
    [styles.feedback, init, onCancel, onSubmit, submitLabel, t]
  );
};

export default ModuleForm;
